import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Box, Typography, Divider, IconButton, Slider, Stack, Tooltip, Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, Chip, Avatar, useTheme, useMediaQuery, CircularProgress } from '@mui/material';
import { PlayArrow, Pause, Forward10, Replay10, Speed, Info, Download, Subtitles, Close, SkipNext, RecordVoiceOver, GraphicEq, MoreVert } from '@mui/icons-material';
import { formatDuration, formatDate, formatTranscriptTimestamp } from '../utils';

const getSpeakerColor = (speakerId) => {
  if (speakerId === null) return '#757575';
  const colors = [
    '#1976d2', '#e53935', '#43a047', '#7b1fa2',
    '#ff9800', '#0097a7', '#c2185b', '#827717',
    '#3949ab', '#6d4c41',
  ];
  const hash = Math.abs(
    speakerId.toString().split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
  );
  return colors[hash % colors.length];
};

const AudioPlayerSection = ({ 
  audioUrl, 
  onTimeUpdate, 
  setAudioRef,
  onDownloadAudio,
  onDownloadSubtitles,
  hasSubtitles = false,
  onHeightChange,
  recordingMetadata = {
    filePath: '',
    fileName: '',
    docId: '',
    duration: 0,
    createdAt: null,
    fileType: '',
    status: '',
    size: 0,
    source: ''
  },
  isKaraokeEnabled = false,
  setIsKaraokeEnabled = () => {},
  showSpeakerDiarization = true,
  onSeek = () => {},
  initialTimeOffsetSeconds = null,
}) => {
  const formatTime = (time) => {
    if (!isFinite(time)) return initialTimeOffsetSeconds != null ? '00:00:00' : '0:00';
    return formatTranscriptTimestamp(time, initialTimeOffsetSeconds, { brackets: false });
  };
  const isIOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const playerRootRef = useRef(null);
  const audioElement = useRef(null);
  const lastReportedHeightRef = useRef(0);
  const resizeFrameRef = useRef(null);
  const lastParentSyncRef = useRef(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speedMenuAnchor, setSpeedMenuAnchor] = useState(null);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [seekValue, setSeekValue] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const speedButtonRef = useRef(null);
  const [metadataDialogOpen, setMetadataDialogOpen] = useState(false);
  const [mobileActionsAnchor, setMobileActionsAnchor] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [currentSpeaker, setCurrentSpeaker] = useState(null);
  const [currentSpeakerText, setCurrentSpeakerText] = useState('');
  const [timelineGradient, setTimelineGradient] = useState(null);
  const speedOptions = [0.5, 1, 1.25, 1.5, 1.75, 2];
  const fileName = recordingMetadata.filePath ? recordingMetadata.filePath.split('/').pop() : recordingMetadata.fileName || recordingMetadata.docId;
  const MIN_UTTERANCE_DURATION = 0.6; // 600ms
  const PARENT_SYNC_INTERVAL_MS = 120;
  const speakerDiarizationEnabled = showSpeakerDiarization === true;

  const syncParentTime = useCallback((time, force = false) => {
    if (!onTimeUpdate || !isFinite(time)) return;

    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    if (!force && now - lastParentSyncRef.current < PARENT_SYNC_INTERVAL_MS) return;

    lastParentSyncRef.current = now;
    onTimeUpdate(time);
  }, [onTimeUpdate]);

  useEffect(() => {
    if (setAudioRef) {
      setAudioRef(audioElement);
    }
  }, [setAudioRef]);

  useEffect(() => {
    if (!onHeightChange || !playerRootRef.current) return;

    const updateHeight = () => {
      const nextHeight = Math.ceil(playerRootRef.current.getBoundingClientRect().height);
      if (nextHeight === lastReportedHeightRef.current) return;
      lastReportedHeightRef.current = nextHeight;
      onHeightChange(nextHeight);
    };

    const scheduleHeightUpdate = () => {
      if (resizeFrameRef.current) {
        cancelAnimationFrame(resizeFrameRef.current);
      }
      resizeFrameRef.current = requestAnimationFrame(updateHeight);
    };

    scheduleHeightUpdate();

    let observer;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(scheduleHeightUpdate);
      observer.observe(playerRootRef.current);
    }
    window.addEventListener('resize', scheduleHeightUpdate);

    return () => {
      observer?.disconnect();
      if (resizeFrameRef.current) {
        cancelAnimationFrame(resizeFrameRef.current);
      }
      window.removeEventListener('resize', scheduleHeightUpdate);
    };
  }, [onHeightChange]);

  useEffect(() => {
    if (audioElement.current) {
      const audio = audioElement.current;
      const loadMetadata = () => {
        const audioDuration = audio.duration;
        if (isFinite(audioDuration)) {
          setDuration(audioDuration);
        }
      };
      const tryGetDuration = () => {
        if (!isFinite(audio.duration) || audio.duration === 0) {
          if (audio.src.toLowerCase().includes('.webm')) {
            setDuration(36000);
          }
        } else {
          setDuration(audio.duration);
        }
      };
      const updateTime = () => {
        if (!isDragging) {
          const audioCurrentTime = audio.currentTime;
          if (isFinite(audioCurrentTime)) {
            setCurrentTime(audioCurrentTime);
            setSeekValue(audioCurrentTime);
            syncParentTime(audioCurrentTime);
            if (!isFinite(duration) || duration === 0) {
              tryGetDuration();
            }
          }
        }
      };
      const handlePlay = () => setIsPlaying(true);
      const handlePause = () => setIsPlaying(false);
      const handleEnded = () => setIsPlaying(false);
      if (audio.readyState >= 2) {
        loadMetadata();
        tryGetDuration();
      }
      audio.addEventListener('timeupdate', updateTime);
      audio.addEventListener('loadedmetadata', loadMetadata);
      audio.addEventListener('durationchange', loadMetadata);
      audio.addEventListener('progress', tryGetDuration);
      audio.addEventListener('canplay', tryGetDuration);
      audio.addEventListener('play', handlePlay);
      audio.addEventListener('pause', handlePause);
      audio.addEventListener('ended', handleEnded);
      return () => {
        audio.removeEventListener('timeupdate', updateTime);
        audio.removeEventListener('loadedmetadata', loadMetadata);
        audio.removeEventListener('durationchange', loadMetadata);
        audio.removeEventListener('progress', tryGetDuration);
        audio.removeEventListener('canplay', tryGetDuration);
        audio.removeEventListener('play', handlePlay);
        audio.removeEventListener('pause', handlePause);
        audio.removeEventListener('ended', handleEnded);
      };
    }
  }, [isDragging, duration, syncParentTime]);

  useEffect(() => {
    const { diarizationStatus, diarizationSegments, speakerNames } = recordingMetadata || {};

    if (speakerDiarizationEnabled && isKaraokeEnabled && diarizationStatus === 'succeeded' && diarizationSegments && diarizationSegments.length > 0) {
      const currentSegment = diarizationSegments.find(
        (seg) => currentTime >= seg.start && currentTime < seg.end
      );

      if (currentSegment) {
        const speakerId = currentSegment.speaker;
        const speakerNumber = parseInt(speakerId.split('_')[1], 10) + 1;
        const speakerName = (speakerNames && speakerNames[speakerId]) || `Խոսող ${speakerNumber}`;
        const speakerColor = getSpeakerColor(speakerId);
        
        setCurrentSpeaker({
          id: speakerId,
          name: speakerName,
          color: speakerColor,
          number: speakerNumber
        });
        setCurrentSpeakerText(`${speakerName}`);
      } else {
        setCurrentSpeaker(null);
        setCurrentSpeakerText('');
      }
    } else {
      setCurrentSpeaker(null);
      setCurrentSpeakerText('');
    }
  }, [currentTime, recordingMetadata, isKaraokeEnabled, speakerDiarizationEnabled]);

  useEffect(() => {
    const { diarizationStatus, diarizationSegments } = recordingMetadata || {};
    const hasDiarization = speakerDiarizationEnabled &&
                          diarizationStatus === 'succeeded' &&
                          diarizationSegments?.length > 0 &&
                          isFinite(duration) &&
                          duration > 0;

    if (!hasDiarization) {
        setTimelineGradient(null);
        return;
    }

    const segments = diarizationSegments.map(seg => ({
      ...seg,
      color: getSpeakerColor(seg.speaker),
      startPercent: (seg.start / duration) * 100,
      endPercent: (seg.end / duration) * 100
    }));
    
    const colorStops = segments.map(seg => 
      `${seg.color} ${seg.startPercent}%, ${seg.color} ${seg.endPercent}%`
    ).join(', ');

    const finalGradient = diarizationSegments.length > 0 
      ? `linear-gradient(to right, ${colorStops}), rgba(0, 0, 0, 0.12)` 
      : 'rgba(0, 0, 0, 0.12)';

    setTimelineGradient(finalGradient);

  }, [recordingMetadata, duration, speakerDiarizationEnabled]);

  if (recordingMetadata.fileType && recordingMetadata.fileType.toLowerCase().includes('webm') && isIOS) {
    return null;
  }

  const handlePlayPause = () => {
    if (audioElement.current) {
      if (isPlaying) {
        audioElement.current.pause();
      } else {
        audioElement.current.play().catch(err => console.error('Playback error:', err));
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleForward = () => {
    if (audioElement.current) {
      const maxTime = isFinite(duration) && duration > 0 ? duration : audioElement.current.currentTime + 3600;
      const newTime = Math.min(audioElement.current.currentTime + 10, maxTime);
      audioElement.current.currentTime = newTime;
      setCurrentTime(newTime);
      setSeekValue(newTime);
      syncParentTime(newTime, true);
    }
  };

  const handleRewind = () => {
    if (audioElement.current) {
      const newTime = Math.max(audioElement.current.currentTime - 10, 0);
      audioElement.current.currentTime = newTime;
      setCurrentTime(newTime);
      setSeekValue(newTime);
      syncParentTime(newTime, true);
    }
  };
  
  const handleSkipToNextSpeaker = () => {
    const { diarizationStatus, diarizationSegments } = recordingMetadata || {};
    if (!audioElement.current || diarizationStatus !== 'succeeded' || !diarizationSegments || diarizationSegments.length === 0) {
      return;
    }

    const currentSegment = diarizationSegments.find(seg => currentTime >= seg.start && currentTime < seg.end);
    const currentSpeakerId = currentSegment ? currentSegment.speaker : null;

    let nextSegment;
    if (currentSpeakerId) {
        nextSegment = diarizationSegments.find(
            seg => seg.start > currentTime && 
                   seg.speaker !== currentSpeakerId && 
                   (seg.end - seg.start) > MIN_UTTERANCE_DURATION
        );
    } else {
        nextSegment = diarizationSegments.find(seg => seg.start > currentTime);
    }
    
    if (nextSegment) {
        const newTime = nextSegment.start;
        audioElement.current.currentTime = newTime;
        setCurrentTime(newTime);
        setSeekValue(newTime);
        syncParentTime(newTime, true);
        onSeek(newTime);
    }
  };

  const handleSliderChange = (_, value) => {
    setSeekValue(value);
  };

  const handleSliderCommitted = (_, value) => {
    if (audioElement.current && isFinite(value)) {
      audioElement.current.currentTime = value;
      setCurrentTime(value);
      setIsDragging(false);
      syncParentTime(value, true);
      onSeek(value);
    }
  };

  const handleSliderMouseDown = () => {
    setIsDragging(true);
  };

  const handleSpeedMenuOpen = (event) => {
    setSpeedMenuAnchor(event.currentTarget);
  };

  const handleSpeedMenuClose = () => {
    setSpeedMenuAnchor(null);
  };

  const handleSpeedChange = (speed) => {
    if (audioElement.current) {
      audioElement.current.playbackRate = speed;
      setPlaybackSpeed(speed);
      handleSpeedMenuClose();
    }
  };

  const handleMetadataDialogOpen = () => {
    setMetadataDialogOpen(true);
  };

  const handleMetadataDialogClose = () => {
    setMetadataDialogOpen(false);
  };

  const handleMobileActionsOpen = (event) => {
    setMobileActionsAnchor(event.currentTarget);
  };

  const handleMobileActionsClose = () => {
    setMobileActionsAnchor(null);
  };

  const handleCycleSpeed = () => {
    const currentIndex = speedOptions.indexOf(playbackSpeed);
    const nextSpeed = speedOptions[(currentIndex + 1) % speedOptions.length];
    if (audioElement.current) {
      audioElement.current.playbackRate = nextSpeed;
      setPlaybackSpeed(nextSpeed);
    }
  };

  const downloadFile = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      const response = await fetch(audioUrl);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = objectUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
      if (typeof onDownloadAudio === 'function') {
        onDownloadAudio();
      }
    } catch (err) {
      console.error('Error downloading file:', err);
    } finally {
      setIsDownloading(false);
    }
  };

  const downloadSubtitlesFile = async () => {
    if (typeof onDownloadSubtitles !== 'function') return;
    try {
      await onDownloadSubtitles();
    } catch (err) {
      console.error('Error downloading subtitles:', err);
    }
  };

  const getStatusChip = (status) => {
    return (
      <Chip
        label={
          status === 'transcribed'
            ? 'Վերծանված'
            : status === 'processing'
            ? 'Ընթացքում է'
            : status === 'uploading'
            ? 'Վերբեռնվում է'
            : 'Խափանված'
        }
        color={
          status === 'transcribed'
            ? 'success'
            : status === 'processing'
            ? 'warning'
            : status === 'uploading'
            ? 'info'
            : 'error'
        }
        size="small"
        variant="outlined"
      />
    );
  };

  const getSourceLabel = (source) => {
    switch(source) {
      case 'upload': return 'Վերբեռնված ֆայլ';
      case 'recording': return 'Ձայնագրություն';
      case 'agent': return 'AI ագենտի զանգ';
      default: return source || 'Անհայտ';
    }
  };

  if (!audioUrl) return null;
  
  const { diarizationStatus, diarizationSegments } = recordingMetadata || {};
  const hasDiarizationData = speakerDiarizationEnabled && diarizationStatus === 'succeeded' && Boolean(diarizationSegments?.length);
  const hasPlaybackStarted = isPlaying || currentTime > 0;
  const showSpeakerStrip = isKaraokeEnabled && hasDiarizationData && hasPlaybackStarted;
  const canSkipToNextSpeaker = (() => {
    if (!speakerDiarizationEnabled || !isKaraokeEnabled || diarizationStatus !== 'succeeded' || !diarizationSegments || diarizationSegments.length === 0) {
      return false;
    }
    const currentSegment = diarizationSegments.find(seg => currentTime >= seg.start && currentTime < seg.end);
    const currentSpeakerId = currentSegment ? currentSegment.speaker : null;

    if (currentSpeakerId) {
      return diarizationSegments.some(
          seg => seg.start > currentTime && 
                 seg.speaker !== currentSpeakerId &&
                 (seg.end - seg.start) > MIN_UTTERANCE_DURATION
      );
    } else {
      return diarizationSegments.some(seg => seg.start > currentTime);
    }
  })();

  return (
    <Box
      ref={playerRootRef}
      sx={{
        position: 'fixed',
        bottom: 0,
        left: { xs: 0, sm: drawerWidth },
        right: 0,
        backgroundColor: 'background.paper',
        boxShadow: '0 -4px 20px rgba(0,0,0,0.1)',
        zIndex: 1000,
        borderTop: '1px solid',
        borderColor: 'divider',
        backdropFilter: 'blur(10px)',
        background: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(250,250,250,0.95) 100%)',
      }}
    >
      {/* Main Player Section */}
      <Box
        sx={{
          pt: { xs: 1, sm: 1.5 },
          pb: { xs: 'calc(env(safe-area-inset-bottom) + 8px)', sm: 1.5 },
          px: { xs: 1.25, sm: 2 }
        }}
      >
        {showSpeakerStrip && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.6 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip
                title={
                  currentSpeaker
                    ? `Հիմա խոսում է: ${currentSpeakerText}`
                    : 'Այս պահին խոսող չի հայտնաբերվել'
                }
                placement="top"
              >
                <Box
                  onClick={() => {
                    if (currentSpeaker) onSeek(currentTime);
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    cursor: 'pointer',
                    py: 0.35,
                    px: 1,
                    borderRadius: 999,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    maxWidth: { xs: 180, sm: 280 },
                    minWidth: { xs: 132, sm: 168 },
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}
                >
                  <Avatar
                    sx={{
                      width: 18,
                      height: 18,
                      backgroundColor: currentSpeaker ? currentSpeaker.color : 'grey.500',
                    }}
                  >
                    <RecordVoiceOver sx={{ fontSize: '0.75rem' }} />
                  </Avatar>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.primary',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {currentSpeaker ? currentSpeakerText : 'Խոսող չկա'}
                  </Typography>
                </Box>
              </Tooltip>
              <Tooltip title="Անցնել հաջորդ խոսողին" placement="top">
                <span>
                  <IconButton
                    onClick={handleSkipToNextSpeaker}
                    size="small"
                    disabled={!canSkipToNextSpeaker}
                    aria-label="skip to next speaker"
                    sx={{ p: 0.5 }}
                  >
                    <SkipNext fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
        )}

        <Box sx={{ display: 'grid', gridTemplateColumns: initialTimeOffsetSeconds != null ? '64px 1fr 64px' : '44px 1fr 44px', alignItems: 'center', gap: 1.75, mb: 1.15 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 700, textAlign: 'left' }}
          >
            {formatTime(currentTime)}
          </Typography>
          <Slider
            value={seekValue}
            max={isFinite(duration) && duration > 0 ? duration : Math.max(100, currentTime * 2)}
            onChange={handleSliderChange}
            onChangeCommitted={handleSliderCommitted}
            onMouseDown={handleSliderMouseDown}
            valueLabelDisplay="auto"
            valueLabelFormat={formatTime}
            sx={{
              height: 6,
              '& .MuiSlider-thumb': { 
                width: 14, 
                height: 14,
                backgroundColor: 'primary.main',
                border: '2px solid #fff',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                },
                '&.Mui-focusVisible': {
                  boxShadow: '0 0 0 8px rgba(25,118,210,0.2)',
                },
              },
                '& .MuiSlider-track': { 
                  height: 6,
                  border: 'none',
                  borderRadius: 3,
                  backgroundColor: speakerDiarizationEnabled && isKaraokeEnabled ? 'rgba(0, 0, 0, 0.25)' : 'primary.main',
                },
                '& .MuiSlider-rail': { 
                  height: 6,
                  opacity: 1,
                  borderRadius: 3,
                  background: speakerDiarizationEnabled && isKaraokeEnabled && timelineGradient ? timelineGradient : 'rgba(0, 0, 0, 0.12)',
                },
              '& .MuiSlider-valueLabel': {
                backgroundColor: 'rgba(0,0,0,0.8)',
                color: 'white',
                fontSize: '0.75rem',
                fontWeight: 500,
              },
            }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 700, textAlign: 'right' }}
          >
            {isFinite(duration) && duration > 0 ? formatTime(duration) : '--:--'}
          </Typography>
        </Box>

        {/* Controls */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {!isMobile ? (
            <Box sx={{ minWidth: hasSubtitles ? '128px' : '96px', display: 'flex', justifyContent: 'flex-start', gap: 0.5 }}>
              <Tooltip title="Մանրամասներ" placement="top">
                <IconButton 
                  onClick={handleMetadataDialogOpen} 
                  color="primary"
                  size="small"
                  sx={{
                    '&:hover': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <Info fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Ներբեռնել ձայնագրությունը" placement="top">
                <IconButton
                  onClick={downloadFile}
                  color="primary"
                  size="small"
                  disabled={isDownloading}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  {isDownloading
                    ? <CircularProgress size={16} color="inherit" />
                    : <Download fontSize="small" />}
                </IconButton>
              </Tooltip>
              {hasSubtitles && (
                <Tooltip title="Ներբեռնել ենթագրերը" placement="top">
                  <IconButton
                    onClick={downloadSubtitlesFile}
                    color="primary"
                    size="small"
                    sx={{
                      '&:hover': {
                        backgroundColor: 'primary.main',
                        color: 'white',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <Subtitles fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ) : (
            <Box sx={{ width: 36 }} />
          )}

          <Stack direction="row" spacing={isMobile ? 0.2 : 0.5} alignItems="center" justifyContent="center">
            <IconButton 
              onClick={handleRewind} 
              aria-label="rewind 10 seconds" 
              size="medium"
              sx={{ 
                width: { xs: 38, sm: 42 }, 
                height: { xs: 38, sm: 42 },
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'action.hover',
                  transform: 'scale(1.03)',
                },
              }}
            >
              <Replay10 fontSize="small" />
            </IconButton>
            <IconButton
              onClick={handlePlayPause}
              aria-label={isPlaying ? 'pause' : 'play'}
              sx={{
                mx: { xs: 0.4, sm: 0.8 },
                backgroundColor: 'primary.main',
                color: 'white',
                width: { xs: 46, sm: 52 },
                height: { xs: 46, sm: 52 },
                boxShadow: '0 4px 12px rgba(25,118,210,0.3)',
                transition: 'all 0.2s ease',
                '&:hover': { 
                  backgroundColor: 'primary.dark',
                  transform: 'scale(1.03)',
                  boxShadow: '0 6px 16px rgba(25,118,210,0.4)',
                },
              }}
              size="medium"
            >
              {isPlaying ? <Pause fontSize="medium" /> : <PlayArrow fontSize="medium" />}
            </IconButton>
            <IconButton 
              onClick={handleForward} 
              aria-label="forward 10 seconds" 
              size="medium"
              sx={{ 
                width: { xs: 38, sm: 42 }, 
                height: { xs: 38, sm: 42 },
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'action.hover',
                  transform: 'scale(1.03)',
                },
              }}
            >
              <Forward10 fontSize="small" />
            </IconButton>
          </Stack>

          {!isMobile ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: { xs: 0, sm: 1 } }}>
              {speakerDiarizationEnabled && (
                <Tooltip title="Կարաոկե նվագարկում" placement="top">
                  <Box
                    onClick={() => setIsKaraokeEnabled(!isKaraokeEnabled)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      py: 0.5,
                      px: 0.75,
                      borderRadius: 1,
                      color: isKaraokeEnabled ? 'primary.main' : 'text.secondary',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <GraphicEq sx={{ fontSize: '1rem', mr: 0.35 }} />
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 700, color: isKaraokeEnabled ? 'primary.main' : 'inherit' }}
                    >
                      {isKaraokeEnabled ? 'ON' : 'OFF'}
                    </Typography>
                  </Box>
                </Tooltip>
              )}
              <Tooltip title="Արագություն" placement="top">
                <Box 
                  ref={speedButtonRef}
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    cursor: 'pointer',
                    py: 0.5,
                    px: 0.75,
                    borderRadius: 1,
                    transition: 'all 0.2s ease',
                    '&:hover': { 
                      backgroundColor: 'action.hover'
                    }
                  }}
                  onClick={handleSpeedMenuOpen}
                >
                  <Typography variant="body2" sx={{ mr: 0.35, fontWeight: 600, fontSize: '0.8rem' }}>
                    {playbackSpeed}x
                  </Typography>
                  <Speed fontSize="small" />
                </Box>
              </Tooltip>
            </Box>
          ) : (
            <IconButton
              onClick={handleMobileActionsOpen}
              aria-label="open player actions"
              size="small"
              color="primary"
              sx={{ width: 36, height: 36 }}
            >
              <MoreVert fontSize="small" />
            </IconButton>
          )}
        </Box>

        <Menu
          anchorEl={speedMenuAnchor}
          open={Boolean(speedMenuAnchor)}
          onClose={handleSpeedMenuClose}
          MenuListProps={{
            'aria-labelledby': 'speed-button',
          }}
          PaperProps={{
            sx: {
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
              borderRadius: 2,
            }
          }}
        >
          {speedOptions.map((speed) => (
            <MenuItem
              key={speed}
              onClick={() => handleSpeedChange(speed)}
              selected={playbackSpeed === speed}
              dense
              sx={{
                fontWeight: playbackSpeed === speed ? 600 : 400,
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                },
              }}
            >
              {speed}x
            </MenuItem>
          ))}
        </Menu>
        <Menu
          anchorEl={mobileActionsAnchor}
          open={Boolean(mobileActionsAnchor)}
          onClose={handleMobileActionsClose}
          PaperProps={{
            sx: {
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
              borderRadius: 2,
              minWidth: 210
            }
          }}
        >
          <MenuItem
            onClick={() => {
              handleMobileActionsClose();
              handleMetadataDialogOpen();
            }}
          >
            <Info fontSize="small" sx={{ mr: 1 }} />
            Մանրամասներ
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleMobileActionsClose();
              downloadFile();
            }}
            disabled={isDownloading}
          >
            {isDownloading
              ? <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
              : <Download fontSize="small" sx={{ mr: 1 }} />}
            Ներբեռնել ձայնագրությունը
          </MenuItem>
          {hasSubtitles && (
            <MenuItem
              onClick={() => {
                handleMobileActionsClose();
                downloadSubtitlesFile();
              }}
            >
              <Subtitles fontSize="small" sx={{ mr: 1 }} />
              Ներբեռնել ենթագրերը
            </MenuItem>
          )}
          {speakerDiarizationEnabled && (
            <MenuItem
              onClick={() => {
                handleMobileActionsClose();
                setIsKaraokeEnabled(!isKaraokeEnabled);
              }}
            >
              <GraphicEq fontSize="small" sx={{ mr: 1 }} />
              Կարաոկե {isKaraokeEnabled ? 'ON' : 'OFF'}
            </MenuItem>
          )}
          <MenuItem
            onClick={() => {
              handleMobileActionsClose();
              handleCycleSpeed();
            }}
          >
            <Speed fontSize="small" sx={{ mr: 1 }} />
            Արագություն {playbackSpeed}x
          </MenuItem>
        </Menu>
      </Box>

      <audio ref={audioElement} style={{ display: 'none' }}>
        <source src={audioUrl} type="audio/mpeg" />
        <source src={audioUrl} type="audio/webm" />
        Ձեր զննարկիչը չի աջակցում աուդիո նվագարկումը:
      </audio>

      <Dialog
        open={metadataDialogOpen}
        onClose={handleMetadataDialogClose}
        disableScrollLock
        aria-labelledby="recording-metadata-dialog-title"
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 12px 48px rgba(0,0,0,0.15)',
          }
        }}
      >
        <DialogTitle id="recording-metadata-dialog-title" sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Ձայնագրության մանրամասներ
            </Typography>
            <IconButton 
              aria-label="close"
              onClick={handleMetadataDialogClose}
              size="small"
              sx={{
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Անուն:
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 'medium', wordBreak: 'break-word' }}>
                {fileName}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Տևողություն:
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                {formatDuration(recordingMetadata.duration || duration)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Ստեղծվել է:
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                {recordingMetadata.createdAt ? formatDate(recordingMetadata.createdAt) : 'Անհայտ'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Ֆորմատ:
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                {recordingMetadata.fileType || ''}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Չափս:
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                {recordingMetadata.size ? `${recordingMetadata.size} ՄԲ` : ''}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Աղբյուր:
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                {getSourceLabel(recordingMetadata.source)}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Կարգավիճակ:
              </Typography>
              <Box sx={{ mt: 1 }}>
                {getStatusChip(recordingMetadata.status)}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          {audioUrl && (
            <Button
              startIcon={isDownloading ? <CircularProgress size={16} color="inherit" /> : <Download />}
              variant="outlined"
              color="primary"
              onClick={downloadFile}
              disabled={isDownloading}
              sx={{ borderRadius: 1.5 }}
            >
              {isDownloading ? 'Ներբեռնվում է...' : 'Ներբեռնել ձայնագրությունը'}
            </Button>
          )}
          <Button 
            onClick={handleMetadataDialogClose} 
            color="primary"
            variant="contained"
            sx={{ borderRadius: 1.5 }}
          >
            Փակել
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const drawerWidth = 240;

export default AudioPlayerSection;
